import { Collection } from "../../domain/Collection";

import { Role, RoleCreationParameters } from "../../domain/Role";
import { useServicesContext } from "../../providers/ServicesProvider";
import { usePaginatedList } from "../usePaginatedList/usePaginatedList";
import { DEFAULT_PAGE_SIZE, SWR_KEY_PREFIX, EVENT_TYPES } from "../../constants/constants";
import { useEffect, useMemo } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { useUpdateSync } from "../useUpdateSync/useUpdateSync";

export const useRoles = ({
    instanceId = "",
    organizationId,
    productId = "",
    projectId,
    searchQuery = "",
    skip = false,
    size = DEFAULT_PAGE_SIZE
}: {
    instanceId?: string;
    organizationId?: string;
    productId?: string;
    projectId?: string;
    searchQuery?: string;
    skip?: boolean;
    size?: number;
}) => {
    const { userService } = useServicesContext();
    const { fireChangeEvent } = useUpdateSync();

    // generate an swrKey when we have the necessary parameters to avoid unnecessary network calls
    const swrKey = useMemo(() => {
        if (!organizationId || skip) return null;
        if (projectId) {
            return `${SWR_KEY_PREFIX.ROLES}/api/organizations/${organizationId}/roles?productId=${productId}&instanceId=${instanceId}&attributeId=${projectId}`;
        }
        return `${SWR_KEY_PREFIX.ROLES}/api/organizations/${organizationId}/roles/search?searchQuery=${searchQuery}&productId=${productId}&instanceId=${instanceId}&size=${size}`;
    }, [organizationId, skip, projectId, productId, instanceId, searchQuery, size]);

    const {
        pageData,
        isLoading: paginationLoading,
        error,
        currentPage,
        setCurrentPage,
        mutate
    } = usePaginatedList<Role>({
        key: swrKey,
        fetcher: (): Promise<Collection<Role>> => {
            // if we have a project id, use the getRoles to filter by attribute
            if (projectId) {
                return userService.getRoles({
                    organizationId: organizationId as string,
                    productId,
                    instanceId,
                    projectId,
                    size,
                    page: currentPage
                });
            }
            return userService.searchRoles({
                organizationId: organizationId as string,
                productId,
                instanceId,
                searchQuery,
                size,
                page: currentPage
            });
        }
    });

    useEffect(() => {
        if (error) {
            datadogRum.addError(error);
        }
    }, [error]);

    const createRole = async (roleDetails: RoleCreationParameters) => {
        const role = await userService.createRole(roleDetails);

        fireChangeEvent({
            type: EVENT_TYPES.ADD_ROLE,
            data: {
                role: { id: roleDetails.id || "", name: roleDetails.name, description: roleDetails.description || "" }
            }
        });
        return role;
    };

    const updateRole = async (roleDetails: RoleCreationParameters) => {
        const result = await userService.updateRole(roleDetails);

        if (roleDetails.id) {
            fireChangeEvent({
                type: EVENT_TYPES.UPDATE_ROLE,
                data: {
                    role: {
                        id: roleDetails.id,
                        description: roleDetails.description || "",
                        ...roleDetails
                    }
                }
            });
        }

        return result;
    };

    const deleteRole = async ({ roleId }: { roleId: string }) => {
        const response = await userService.deleteRole({ roleId });

        fireChangeEvent({
            type: EVENT_TYPES.DELETE_ROLE,
            data: {
                role: { id: roleId, name: "", description: "" }
            }
        });

        return response;
    };

    return {
        createRole,
        deleteRole,
        // the filter for isActive can be removed once the API support the isActive parameter
        roles: pageData?.items?.filter((r) => r.isActive),
        isLoading: paginationLoading || (!error && !pageData),
        error,
        currentPage,
        setCurrentPage,
        pageSize: DEFAULT_PAGE_SIZE,
        totalCount: pageData?.totalCount || 0,
        updateRole,
        revalidate: mutate
    };
};
